/**
 * CR-7275 — is this line's order price above, on, or below its contract?
 *
 * Sarah's rule, confirmed 10 Sep 2026, and it is strict: there is no
 * threshold and no tolerance.
 *
 *   unit price GREATER than contract price   over    red
 *   unit price EXACTLY EQUAL to contract     exact   green
 *   unit price LOWER than contract price     under   amber
 *
 * A LOT-priced contract (Sarah, 17 Sep 2026: "we do need to understand if
 * there is a variance between the LOT price to the total price") is held to
 * the same three bands, comparing the line's total, unit price x quantity,
 * with the lot price. That is the comparison PPV already makes for such a
 * line: `get_contracted_price` turns a lot price into lot / quantity, as
 * legacy's PPV SQL does (`NetworkSQL.cs`, "LotPrice > 0 AND QtyOrdered > 0").
 *
 * It does not vary by site and there is nothing to configure — an earlier
 * draft of the ticket proposed a tunable 5% band held in Admin Setup, and
 * that was withdrawn.
 *
 * One verdict drives both the contract price boxes and the PPV box. PPV is
 * `(unit_price - contracted) x quantity` (purchasing/services/ppv_service.py),
 * so it is this same comparison already multiplied out: positive means we
 * paid over contract, which is the red case. Deriving both from here rather
 * than reading PPV's sign separately keeps the box and the prices beside it
 * from ever disagreeing — and it avoids PPV's 2dp rounding turning a real
 * difference into an apparent exact match.
 *
 * Both operands come from the same ContractPricePart: the screen's
 * `contract_unit_price` is `contract_price_part.unit_price`, and PPV's
 * `contracted` comes from `PPVService.get_contracted_price`, which resolves
 * that same row. So there is one number being compared, not two that happen
 * to look alike.
 */

/** Where the order price sits against the contract, or null if not comparable. */
export type ContractPriceVariance = 'over' | 'exact' | 'under';

/** A decimal string split into sign and scaled integer digits. */
interface ScaledDecimal {
  negative: boolean;
  digits: string;
  scale: number;
}

const DECIMAL_PATTERN = /^\s*([+-]?)(\d*)(?:\.(\d*))?\s*$/;

function parseDecimal(value: string): ScaledDecimal | null {
  const match = DECIMAL_PATTERN.exec(value);
  if (!match) return null;
  const [, sign, whole = '', fraction = ''] = match;
  if (!whole && !fraction) return null;
  return {
    negative: sign === '-',
    digits: `${whole || '0'}${fraction}`,
    scale: fraction.length,
  };
}

/**
 * Compares two decimal strings exactly, without going through a float.
 *
 * "Exact means exact" is the whole point of the green band, and both operands
 * are DecimalFields serialised as strings, so they arrive at whatever scale
 * the column was declared with: `349.59` and `349.590` are the same price and
 * have to compare equal. Parsing to `number` would mostly work and then
 * quietly not — 0.1 + 0.2 arithmetic is the reason the ticket says never
 * through a float — so both sides are scaled to a common number of decimal
 * places and compared as BigInt.
 *
 * @returns -1, 0 or 1 as with any comparator, or null if either side is not a
 *   decimal we can read.
 */
export function compareDecimalStrings(a: string, b: string): -1 | 0 | 1 | null {
  const left = parseDecimal(a);
  const right = parseDecimal(b);
  if (!left || !right) return null;

  const scale = Math.max(left.scale, right.scale);
  const scaled = (d: ScaledDecimal) => {
    const padded = d.digits + '0'.repeat(scale - d.scale);
    // `-0` and `0` are the same number; normalise before applying the sign.
    const magnitude = BigInt(padded);
    return d.negative ? -magnitude : magnitude;
  };

  const l = scaled(left);
  const r = scaled(right);
  if (l < r) return -1;
  if (l > r) return 1;
  return 0;
}

/** 10^n as a BigInt. Built from a string: the build targets ES5, which has no `**` on bigint. */
function pow10(n: number): bigint {
  return BigInt(`1${'0'.repeat(n)}`);
}

/** A parsed decimal as a signed BigInt of its digits; its value is digits / 10^scale. */
function toBigInt(d: ScaledDecimal): bigint {
  const magnitude = BigInt(d.digits);
  return d.negative ? -magnitude : magnitude;
}

/**
 * Compares `a x b` with `c` exactly, without going through a float.
 *
 * The lot-priced case needs it: the order's total for the line is its unit
 * price times its quantity, and that product is what a lot price is set
 * against. Dividing the lot price by the quantity instead would need
 * rounding, and rounding is what the green band cannot afford.
 *
 * @returns -1, 0 or 1 as `a x b` is below, equal to or above `c`, or null if
 *   any side is not a decimal we can read.
 */
export function compareDecimalProduct(a: string, b: string, c: string): -1 | 0 | 1 | null {
  const x = parseDecimal(a);
  const y = parseDecimal(b);
  const z = parseDecimal(c);
  if (!x || !y || !z) return null;

  // The product carries scale x.scale + y.scale; bring both sides to the larger.
  const productScale = x.scale + y.scale;
  const scale = Math.max(productScale, z.scale);
  const product = toBigInt(x) * toBigInt(y) * pow10(scale - productScale);
  const target = toBigInt(z) * pow10(scale - z.scale);
  if (product < target) return -1;
  if (product > target) return 1;
  return 0;
}

/** The fields this needs — satisfied by PurchaseOrderLine. */
export interface ContractPricedLine {
  unit_price?: string | null;
  contract_unit_price?: string | null;
  /** Set instead of `contract_unit_price` on a lot-priced contract part. */
  contract_lot_price?: string | null;
  quantity?: string | null;
  /** The stored PPV; null when the PPV writer found no contracted price. */
  ppv?: string | null;
  /** Null exactly when the line resolved to no ContractPricePart. */
  contract_id?: number | null;
}

/**
 * A readable decimal above zero -- the test for "this contract part is priced
 * this way". The model's own check is truthiness, so a part can hold a unit
 * price of 0 beside a real lot price; "0.0000" is a truthy string, so the
 * screen must use this, not `!!value`, to pick the box that carries the
 * verdict.
 */
export function isPositive(value: string | null | undefined): value is string {
  return !!value && compareDecimalStrings(value, '0') === 1;
}

/**
 * Whether a contract was resolved for this line at all.
 *
 * `contract_id` is serialised from `contract_price_part.contract_id`
 * (CR-7266), so it is null on precisely the lines with no contract part —
 * 99.65% of the book at the time of writing. Those get the empty state and no
 * colour, rather than being coloured as though they were on contract.
 */
export function hasContract(line: ContractPricedLine): boolean {
  return line.contract_id != null;
}

/**
 * The three-way verdict for a line, or null when there is nothing to compare.
 *
 * Null covers a line with no contract, and a line whose prices are missing or
 * unreadable. A null verdict must render no colour at all.
 *
 * The unit price wins when the part carries one, else the lot price, which
 * is the precedence `PPVService.get_contracted_price` applies. A lot-priced
 * line is coloured only when it has a PPV: the writer needs the line's
 * stored quantity for that, and where it found none there is no variance for
 * the colour to describe.
 *
 * A customer on schedule lines gets no lot verdict at all (Aaron, 6 Oct 2026:
 * schedule-line behaviour is left alone). There the screen's quantity is the
 * schedule sum while PPV was written from the stored header, so a verdict on
 * the screen's quantity could contradict the PPV beside it.
 */
export function getContractPriceVariance(
  line: ContractPricedLine,
  usesScheduleLines = false,
): ContractPriceVariance | null {
  if (!hasContract(line)) return null;
  const ordered = line.unit_price;
  if (!ordered) return null;

  let comparison: -1 | 0 | 1 | null = null;
  if (isPositive(line.contract_unit_price)) {
    comparison = compareDecimalStrings(ordered, line.contract_unit_price);
  } else if (!usesScheduleLines && isPositive(line.contract_lot_price)
             && isPositive(line.quantity) && line.ppv != null) {
    comparison = compareDecimalProduct(ordered, line.quantity, line.contract_lot_price);
  }
  if (comparison === null) return null;
  if (comparison > 0) return 'over';
  if (comparison < 0) return 'under';
  return 'exact';
}

/**
 * Text colour for a verdict. Null verdict means no class, so a line with
 * nothing to compare stays visually neutral.
 */
export function varianceTextClass(variance: ContractPriceVariance | null): string {
  if (variance === 'over') return 'text-red-700 dark:text-red-400';
  if (variance === 'under') return 'text-amber-700 dark:text-amber-400';
  if (variance === 'exact') return 'text-green-700 dark:text-green-400';
  return '';
}

/** Shown in place of a blank, so an empty box reads as explained, not broken. */
export const NO_CONTRACT_TEXT = 'No contract';

/**
 * Contract parts are priced per unit or per lot, never both
 * (purchasing/models/contract.py, enforced by the importer), so whichever
 * box the part does not use is empty by design and says so instead of
 * taking a colour it could never earn.
 */
export const LOT_NOT_APPLICABLE_TEXT = 'Not applicable, unit priced';
export const UNIT_NOT_APPLICABLE_TEXT = 'Not applicable, lot priced';
